import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";

/**
 * `Encounter.meta.tag` codes recording how far an Encounter has progressed with its billable
 * Services, or that a user dismissed it from the queue by hand.
 *
 * `services-not-sent` is written by the charge capture, when signing an Encounter writes Services for
 * it — see `captureEncounterChargeItems`. `services-sent` is written by the billing tab's
 * `Send to Services`, once every Service the Encounter still had waiting has gone — see
 * `sendEncounterServices`. `dismissed` is written by Missing Services itself, when the user confirms
 * Remove.
 *
 * There are four states but only three codes, because carrying no code from this system is itself a
 * state:
 * - no code → the Encounter has no Services at all.
 * - `services-not-sent` → it has Services and at least one is unsent.
 * - `services-sent` → it has Services and none of them are still waiting to be sent.
 * - `dismissed` → a user removed it from the queue by hand.
 *
 * The work queue is everything that is not `services-sent` or `dismissed`, so the search excludes
 * both codes. The remaining two states come back from it, and each row's state is read off the tag
 * it arrived with, so no second query is needed.
 *
 * Every write goes through `setEncounterServicesStatus`, which replaces any existing code from this
 * system rather than appending, so an Encounter never carries more than one at once. The CodeSystem
 * resource is seeded from `app/fhir/seed-data/code-systems/qh-encounter-services-status.json`.
 */
export const EncounterServicesStatusCode = {
  NotSent: "services-not-sent",
  Sent: "services-sent",
  Dismissed: "dismissed",
} as const;

/** The `system|code` token for fully-sent Encounters, in the form a FHIR `_tag` search expects. */
export const ENCOUNTER_SERVICES_SENT_TAG = `${QuiltedHealthCodeSystems.EncounterServicesStatus}|${EncounterServicesStatusCode.Sent}`;

/** The `system|code` token for Encounters holding at least one unsent Service. */
export const ENCOUNTER_SERVICES_NOT_SENT_TAG = `${QuiltedHealthCodeSystems.EncounterServicesStatus}|${EncounterServicesStatusCode.NotSent}`;

/** The `system|code` token for Encounters a user dismissed from the Missing Services queue. */
export const ENCOUNTER_SERVICES_DISMISSED_TAG = `${QuiltedHealthCodeSystems.EncounterServicesStatus}|${EncounterServicesStatusCode.Dismissed}`;

/**
 * Both codes the Missing Services queue excludes, comma-joined for a single `_tag:not` search value
 * — FHIR treats comma-separated token values as an OR-list, so this excludes either one.
 */
export const ENCOUNTER_SERVICES_EXCLUDED_TAGS = [
  ENCOUNTER_SERVICES_SENT_TAG,
  ENCOUNTER_SERVICES_DISMISSED_TAG,
].join(",");

/**
 * Every known code from this system, comma-joined for a single `_tag:not` search value. An Encounter
 * matching this exclusion carries no code at all, so it is the direct count of "no Services" — not a
 * number derived by subtracting two other counts.
 */
export const ENCOUNTER_SERVICES_ANY_CODE_TAGS = [
  ENCOUNTER_SERVICES_SENT_TAG,
  ENCOUNTER_SERVICES_NOT_SENT_TAG,
  ENCOUNTER_SERVICES_DISMISSED_TAG,
].join(",");
